/* Version: 3.5.149 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState } from 'react';
import { useFocusWithin, useFocusRing } from 'react-aria';
import PropTypes from 'prop-types';
import Errors from '../Errors/Errors.js';
import Label from '../Label/Label.js';
import { InputWrapper, AdornmentWrapper, IconWrapper, DashedUnderline } from '../Select/styledComponents.js';
import Stack from '../Stack/Stack.js';
import i18n from '../../lang/i18n/index.js';
import { safeRest, hasErrors } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { ADORNMENT_POSITION, TEXT_ALIGN, INPUT_VARIANT } from './constants.js';
import ShowPasswordToggle from './ShowPasswordToggle.js';
import { Root, LabelWrapper, OptionalText, Input } from './styledComponents.js';
import { FORMAT } from './utils.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';
import useToggle from '../../hooks/useToggle.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["adornment", "adornmentPosition", "ariaDescribedBy", "ariaLabel", "ariaRequired", "autoComplete", "callouts", "className", "customStyle", "datalist", "disabled", "errors", "forceUpper", "formatChars", "icon", "id", "isIconClickable", "isPassword", "label", "mask", "maskChar", "onChange", "onBlur", "onFocus", "outline", "required", "requiredDecorator", "showOptionalLabel", "textAlign", "tooltip", "value", "variant"];
var TextInput = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var adornment = _ref.adornment,
    _ref$adornmentPositio = _ref.adornmentPosition,
    adornmentPosition = _ref$adornmentPositio === undefined ? ADORNMENT_POSITION.LEFT : _ref$adornmentPositio,
    ariaDescribedBy = _ref.ariaDescribedBy,
    ariaLabel = _ref.ariaLabel,
    ariaRequired = _ref.ariaRequired,
    autoComplete = _ref.autoComplete,
    _ref$callouts = _ref.callouts,
    callouts = _ref$callouts === undefined ? false : _ref$callouts,
    className = _ref.className,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {},
      iconStyle: {},
      inputWrapperStyle: {},
      inputStyle: {},
      labelStyle: {},
      adornmentStyle: {}
    } : _ref$customStyle,
    datalist = _ref.datalist,
    disabled = _ref.disabled,
    _ref$errors = _ref.errors,
    errors = _ref$errors === undefined ? [] : _ref$errors,
    _ref$forceUpper = _ref.forceUpper,
    forceUpper = _ref$forceUpper === undefined ? false : _ref$forceUpper,
    _ref$formatChars = _ref.formatChars,
    formatChars = _ref$formatChars === undefined ? FORMAT : _ref$formatChars,
    icon = _ref.icon,
    controlledId = _ref.id,
    _ref$isIconClickable = _ref.isIconClickable,
    isIconClickable = _ref$isIconClickable === undefined ? false : _ref$isIconClickable,
    _ref$isPassword = _ref.isPassword,
    isPassword = _ref$isPassword === undefined ? false : _ref$isPassword,
    _ref$label = _ref.label,
    label = _ref$label === undefined ? '' : _ref$label,
    mask = _ref.mask,
    _ref$maskChar = _ref.maskChar,
    maskChar = _ref$maskChar === undefined ? '' : _ref$maskChar,
    onChange = _ref.onChange,
    _onBlur = _ref.onBlur,
    _onFocus = _ref.onFocus,
    outline = _ref.outline,
    required = _ref.required,
    requiredDecorator = _ref.requiredDecorator,
    showOptionalLabel = _ref.showOptionalLabel,
    _ref$textAlign = _ref.textAlign,
    textAlign = _ref$textAlign === undefined ? TEXT_ALIGN.LEFT : _ref$textAlign,
    tooltip = _ref.tooltip,
    value = _ref.value,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? INPUT_VARIANT.BASIC : _ref$variant,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('input-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var lang = useLang();
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    adornmentStyle = _safeCustomStyle.adornmentStyle,
    iconStyle = _safeCustomStyle.iconStyle,
    inputWrapperStyle = _safeCustomStyle.inputWrapperStyle,
    inputStyle = _safeCustomStyle.inputStyle,
    rootStyle = _safeCustomStyle.rootStyle;
  var _useToggle = useToggle(false),
    _useToggle2 = _slicedToArray(_useToggle, 2),
    pVisible = _useToggle2[0],
    pVisibleToggle = _useToggle2[1];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];
  var _useFocusWithin = useFocusWithin({
      isDisabled: disabled,
      onFocusWithinChange: function onFocusWithinChange(isFocusWithin) {
        return setIsFocused(isFocusWithin);
      }
    }),
    focusWithinProps = _useFocusWithin.focusWithinProps;
  var _useFocusRing = useFocusRing(),
    isFocusVisible = _useFocusRing.isFocusVisible,
    focusProps = _useFocusRing.focusProps;
  var hasAdornment = typeof adornment !== 'undefined';
  var hasIcon = typeof icon !== 'undefined';
  var hasDataList = (datalist === null || datalist === undefined ? undefined : datalist.length) > 0;
  var datalistId = "".concat(id, "-list");
  return /*#__PURE__*/jsxs(Root, {
    className: className,
    rootStyle: rootStyle,
    children: [label && /*#__PURE__*/jsxs(LabelWrapper, {
      children: [/*#__PURE__*/jsx(Label, {
        htmlFor: id,
        customStyle: {
          labelStyle: customStyle.labelStyle
        },
        tooltip: tooltip,
        children: requiredDecorator ? /*#__PURE__*/jsxs(Stack, {
          direction: "row",
          gap: "0.25rem",
          children: [label, requiredDecorator]
        }) : label
      }), !required && showOptionalLabel && /*#__PURE__*/jsx(OptionalText, {
        theme: theme,
        children: i18n[lang].common.labels.optional
      })]
    }), /*#__PURE__*/jsxs(InputWrapper, _objectSpread(_objectSpread({}, focusWithinProps), {}, {
      "aria-invalid": outline || hasErrors(errors),
      "data-adornmentposition": hasAdornment ? adornmentPosition : undefined,
      "data-disabled": disabled,
      "data-focused": isFocused,
      "data-focusvisible": isFocusVisible,
      "data-hasadornment": hasAdornment,
      "data-hasicon": hasIcon,
      "data-testid": "input-wrapper",
      "data-variant": variant,
      error: outline || hasErrors(errors),
      inputWrapperStyle: inputWrapperStyle,
      theme: theme,
      variant: variant,
      children: [hasAdornment && adornmentPosition === ADORNMENT_POSITION.LEFT && /*#__PURE__*/jsx(AdornmentWrapper, {
        adornmentStyle: adornmentStyle,
        "data-variant": variant,
        children: adornment
      }), /*#__PURE__*/jsx(Input, _objectSpread({
        "aria-describedby": hasErrors(errors) ? "".concat(id, "-errors") : ariaDescribedBy,
        autoComplete: isPassword ? autoComplete || 'off' : autoComplete,
        "aria-label": ariaLabel,
        "aria-required": ariaRequired,
        "data-forceuppercase": forceUpper,
        "data-ispassword": isPassword,
        "data-testid": "text-input",
        "data-variant": variant,
        disabled: disabled,
        formatChars: formatChars,
        icon: icon,
        id: id,
        inputStyle: inputStyle,
        list: hasDataList ? datalistId : null,
        mask: mask,
        maskChar: maskChar,
        onBlur: function onBlur(e) {
          focusProps === null || focusProps === undefined || focusProps.onBlur(e);
          handle(_onBlur)(e);
        },
        onChange: handle(onChange),
        onFocus: function onFocus(e) {
          focusProps === null || focusProps === undefined || focusProps.onFocus(e);
          handle(_onFocus)(e);
        },
        ref: ref,
        required: required,
        textAlign: textAlign,
        theme: theme,
        type: isPassword && !pVisible ? 'password' : 'text',
        value: value
      }, safeProps)), !isPassword && adornmentPosition !== ADORNMENT_POSITION.RIGHT && hasIcon && /*#__PURE__*/jsx(IconWrapper, {
        "aria-hidden": !isIconClickable,
        "data-variant": variant,
        iconStyle: iconStyle,
        isClickable: isIconClickable,
        children: icon
      }), !isPassword && hasAdornment && adornmentPosition === ADORNMENT_POSITION.RIGHT && /*#__PURE__*/jsx(AdornmentWrapper, {
        adornmentStyle: adornmentStyle,
        "data-variant": variant,
        children: adornment
      }), isPassword && /*#__PURE__*/jsx(ShowPasswordToggle, {
        id: "pwd-toggle-".concat(id),
        show: pVisible,
        toggle: pVisibleToggle,
        theme: theme
      }), variant === INPUT_VARIANT.BASIC_LINE_DASHED ? /*#__PURE__*/jsx(DashedUnderline, {
        "aria-hidden": true,
        theme: theme
      }) : null]
    })), hasDataList && /*#__PURE__*/jsx("datalist", {
      id: datalistId,
      "data-testid": datalistId,
      children: datalist.map(function (item) {
        return /*#__PURE__*/jsx("option", {
          value: item,
          children: item
        }, item);
      })
    }), /*#__PURE__*/jsx(Errors, {
      id: id,
      callouts: callouts,
      errors: errors
    })]
  });
});
process.env.NODE_ENV !== "production" ? TextInput.propTypes = {
  /**
   * Adornment character.
   */
  adornment: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Adornment position.
   */
  adornmentPosition: PropTypes.oneOf([ADORNMENT_POSITION.LEFT, ADORNMENT_POSITION.RIGHT]),
  /**
   * ARIA attribute [`aria-describedby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-describedby)
   */
  ariaDescribedBy: PropTypes.string,
  /**
   * ARIA attribute [`aria-label`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label).
   */
  ariaLabel: PropTypes.string,
  /**
   * ARIA attribute [`aria-required`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-required).
   */
  ariaRequired: PropTypes.bool,
  /**
   * HTML [`autocomplete`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete) attribute.
   */
  autoComplete: PropTypes.string,
  /**
   * HTML [`autofocus`](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autofocus) attribute.
   */
  autoFocus: PropTypes.bool,
  /**
   * Sets `aria-atomic=true`, `aria-live=polite` and `role=alert` on error messages (ensures errors are announced by screen readers as soon as they happen).
   */
  callouts: PropTypes.bool,
  /**
   * Style overrides.
   *
   * **NOTE:** `inputFlexWrapperStyle` has been deprecated as of [v3.4](/docs/v3-4-release)
   */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    iconStyle: PropTypes.object,
    inputWrapperStyle: PropTypes.object,
    inputStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    adornmentStyle: PropTypes.object
  }),
  /**
   * HTML [`datalist`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist) attribute.
   */
  datalist: PropTypes.arrayOf(PropTypes.string),
  /**
   * HTML [`disabled`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled) attribute.
   */
  disabled: PropTypes.bool,
  /**
   * An array of error messages to be displayed.
   */
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    msg: PropTypes.any,
    isError: PropTypes.bool
  }))]),
  /**
   * Force text in the input to be uppercase (eg. for Postal Code fields)
   */
  forceUpper: PropTypes.bool,
  /**
   * Configuration to override default mask format characters (defaults to `TextInput.FORMAT`).
   *
   * More info: [here](https://github.com/sanniassin/react-input-mask/tree/v2#formatchars--object)
   */
  formatChars: PropTypes.shape({
    '[<char>]': PropTypes.string
  }),
  /**
   * [Icon](/docs/icons) component to show with the input.
   */
  icon: PropTypes.node,
  /**
   * Global [`id`](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id) attribute.
   */
  id: PropTypes.string,
  /**
   * Enable click events for icon by changing `pointer-events` from "none" to "auto".
   */
  isIconClickable: PropTypes.bool,
  /**
   * Indicate whether input is a password field (shows password toggle, sets appropriate `type` & `autocomplete`).
   */
  isPassword: PropTypes.bool,
  /**
   * Label for the input.
   */
  label: PropTypes.node,
  /**
   * HTML [`maxlength`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/maxlength) attribute. Specifies the maximum number of characters that can be entered in a text-type input.
   */
  maxLength: PropTypes.number,
  /**
   * Input mask (uses [`react-input-mask` (v2)](https://github.com/sanniassin/react-input-mask/tree/v2))
   *
   * List of accepted formats: [here](https://github.com/sanniassin/react-input-mask/blob/v2/README.md#mask--string)
   * (eg. for postal code A1B 2C3, the mask would be "a9a 9a9")
   *
   * Note that there is a bug when using the `mask` prop with refs. See [the note under Props](#props).
   */
  mask: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Input mask for unfilled spaces that matches mask. Should be a single character such as `_` or `-`.
   */
  maskChar: PropTypes.string,
  /**
   * Input `onblur` handler.
   */
  onBlur: PropTypes.func,
  /**
   * Input [`onchange`](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event) handler.
   */
  onChange: PropTypes.func,
  /**
   * Input `onfocus` handler.
   */
  onFocus: PropTypes.func,
  /**
   * Flag to set error styling without displaying errors; useful if handling errors yourself using the [Errors](/docs/errors) component.
   */
  outline: PropTypes.bool,
  /**
   * HTML [`placeholder`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/text#placeholder) attribute.
   */
  placeholder: PropTypes.string,
  /**
   * HTML [`readonly`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/text#readonly) attribute.
   */
  readOnly: PropTypes.bool,
  /**
   * HTML [`required`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/required) attribute.
   */
  required: PropTypes.bool,
  /**
   * Text that is appended to label for elements marked as required.
   */
  requiredDecorator: PropTypes.string,
  /**
   * Indicate whether the "Optional" label should be displayed.
   */
  showOptionalLabel: PropTypes.bool,
  /**
   * Alignment of text within the input.
   */
  textAlign: PropTypes.oneOf([TEXT_ALIGN.LEFT, TEXT_ALIGN.RIGHT, TEXT_ALIGN.CENTER]),
  /**
   * [Tooltip](/docs/tooltip) displayed beside the label.
   */
  tooltip: componentWithName('Tooltip'),
  /**
   * Controlled value of the input.
   */
  value: PropTypes.string,
  /**
   * Specifies which style of input to use.
   */
  variant: PropTypes.oneOf([INPUT_VARIANT.BASIC, INPUT_VARIANT.BASIC_LINE, INPUT_VARIANT.BASIC_LINE_DASHED])
} : undefined;
process.env.NODE_ENV !== "production" ? TextInput.displayName = 'TextInput' : undefined;

export { TextInput as default };
